/**
 * Portfolio Composition scatter view.
 * X = share of portfolio (by count or spend), Y = KPI performance.
 * Quadrant labels show over/under-represented segments.
 *
 * FR-41: By count / By spend toggle
 * FR-42: KPI dropdown
 * FR-43: Quadrant reference lines + labels
 * FR-44: Dot size proportional to creativeCount, small sample flagged
 *
 * Phase 33:
 * - PCFG-01: User-selectable GroupField dropdown (Dimensions / Taxonomy / Placement).
 * - PCFG-03: shareBasis local state flows into useComposition (previously cosmetic).
 * - PCFG-06: Zero-variance empty state names the chosen field + suggests alternatives.
 * - PCFG-07: Legacy `?mode=dimension` URL backcompat read on mount.
 */

import { PieChart } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Label,
} from 'recharts';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SkeletonCard } from '@/components/dashboard/shared/SkeletonLoaders';
import { useAuth } from '@/context/AuthContext';
import { useComposition } from '@/hooks/useDashboard';
import { useProjects } from '@/hooks/useProjects';
import { fmtPct, fmtKpiValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import { META_KPI_CATALOG } from '@/types/dashboard';
import type {
  CompositionSegment,
  GroupField,
  GroupFieldWithLabel,
  ShareBasis,
} from '@/types/dashboard';

// ============================================================================
// HELPERS
// ============================================================================

interface DotPayload extends CompositionSegment {
  x: number;
  y: number;
  isSmall: boolean;
  color: string;
}

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: DotPayload;
}

function CustomDot({ cx = 0, cy = 0, payload }: CustomDotProps) {
  if (!payload) return null;
  const r = payload.isSmall ? 5 : Math.min(8 + payload.creativeCount * 0.3, 14);
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={payload.color}
        fillOpacity={0.7}
        stroke={payload.isSmall ? '#8B95A7' : 'none'}
        strokeWidth={payload.isSmall ? 1.5 : 0}
      />
      <text
        x={cx}
        y={cy - r - 3}
        textAnchor="middle"
        fontSize={9}
        fill="#5C677A"
      >
        {payload.value}
      </text>
    </g>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PortfolioComposition() {
  const { filters, activeTemplate } = useDashboardContext();
  const { currentTeam } = useAuth();
  const { data: allProjects } = useProjects(currentTeam?.teamId);
  const [searchParams] = useSearchParams();

  const primaryKpiId = activeTemplate?.kpiConfig?.primaryKpiId ?? null;
  const defaultKpiId = primaryKpiId ?? META_KPI_CATALOG[0]?.id ?? 'ctr';

  const [selectedKpiId, setSelectedKpiId] = useState(defaultKpiId);

  // Resolve the active project (for taxonomy schema) from the project filter.
  // In aggregate mode (projectId === 'all') we fall back to no taxonomy — the
  // dashboard's project-level taxonomy does not apply cleanly across projects.
  const activeProject = useMemo(() => {
    if (!allProjects || filters.projectId === 'all') return null;
    return allProjects.find((p) => p.projectId === filters.projectId) ?? null;
  }, [allProjects, filters.projectId]);

  // Build available group fields from template diagnosisDimensions + project
  // taxonomySchema.fields + the hardcoded placement option. Dimensions keep
  // their template-defined order; taxonomy is sorted alphabetically; placement
  // is a single synthetic option. See Phase 33 CONTEXT "Grouping Field Model".
  const availableGroupFields = useMemo<GroupFieldWithLabel[]>(() => {
    const fields: GroupFieldWithLabel[] = [];

    // Dimensions — preserve template-defined order, skip disabled
    for (const dim of activeTemplate?.diagnosisDimensions ?? []) {
      if (dim.enabled === false) continue;
      fields.push({ kind: 'dimension', id: dim.dimensionId, label: dim.name });
    }

    // Taxonomy — read from active project's taxonomy schema, alphabetized
    const taxonomyFields = (activeProject?.taxonomySchema?.fields ?? [])
      .slice() // copy before sort
      .sort((a, b) => (a.name || a.fieldId).localeCompare(b.name || b.fieldId));
    for (const tf of taxonomyFields) {
      fields.push({ kind: 'taxonomy', id: tf.fieldId, label: tf.name || tf.fieldId });
    }

    // All Creatives — synthetic option for individual creative view
    fields.push({ kind: 'creative', id: 'all', label: 'All Creatives' });

    // Placement — single hardcoded option
    fields.push({ kind: 'placement', id: 'placement', label: 'Meta Placement' });

    return fields;
  }, [activeTemplate, activeProject]);

  // Lookup from field id → human-readable label (used by tooltip).
  const fieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of availableGroupFields) {
      map[f.id] = f.label;
    }
    return map;
  }, [availableGroupFields]);

  // KPI unit suffix for Y-axis tick formatting.
  const kpiUnit = META_KPI_CATALOG.find(k => k.id === selectedKpiId)?.unit ?? '%';

  // Resolve the desired groupField from URL + template. Returns null when
  // `activeTemplate` is not yet loaded — a dedicated seeding useEffect below
  // commits the resolved value once the template arrives. This avoids the
  // mount-time race where `firstDim` is undefined and the resolution silently
  // falls through to placement. See 33-VERIFICATION.md Blockers 2 and 3.
  //
  // Precedence (once template is loaded):
  //   1. New-shape URL params (groupFieldKind + groupFieldId)
  //   2. Legacy `mode=placement` → placement
  //   3. Legacy `mode=taxonomy` → first taxonomy field
  //   4. Legacy `mode=dimension`, `mode=count`, or absent → first enabled dim
  //   5. Terminal fallback → placement (only if template has 0 dims AND 0 taxonomy)
  const resolveGroupField = (
    template: typeof activeTemplate,
    taxonomyFields: ReadonlyArray<GroupFieldWithLabel>
  ): GroupField | null => {
    if (!template) return null;

    const urlKind = searchParams.get('groupFieldKind');
    const urlId = searchParams.get('groupFieldId');
    if (
      urlKind &&
      urlId &&
      (urlKind === 'dimension' || urlKind === 'taxonomy' || urlKind === 'placement' || urlKind === 'creative')
    ) {
      return { kind: urlKind as GroupField['kind'], id: urlId };
    }

    const legacyMode = searchParams.get('mode');
    if (legacyMode === 'placement') {
      return { kind: 'placement', id: 'placement' };
    }
    if (legacyMode === 'taxonomy') {
      const firstTax = taxonomyFields[0];
      if (firstTax) return { kind: 'taxonomy', id: firstTax.id };
    }

    // Default path: `mode=dimension`, `mode=count`, or no `mode` param at all —
    // all translate to the first enabled diagnosis dimension.
    const firstDim =
      template.diagnosisDimensions?.find((d) => d.enabled !== false) ??
      template.diagnosisDimensions?.[0];
    if (firstDim) return { kind: 'dimension', id: firstDim.dimensionId };

    // Terminal fallback — template loaded but has zero enabled dims AND no
    // taxonomy → placement. Only reached in degenerate setups.
    return { kind: 'placement', id: 'placement' };
  };

  // null sentinel means "template still loading — don't commit yet".
  // Attempt a synchronous resolution at mount in case activeTemplate is
  // already available (cached template / hot-reload / test shape). When it
  // isn't, the seeding useEffect below will commit the real value once the
  // template transitions from null to loaded.
  const [groupField, setGroupField] = useState<GroupField | null>(() =>
    resolveGroupField(
      activeTemplate,
      // availableGroupFields isn't in scope yet (declared above but refers
      // to state we still need to track for the mount-time hot path). Reuse
      // the same taxonomy-ordering logic inline here; this branch only runs
      // when activeTemplate is already non-null, which is rare.
      (activeProject?.taxonomySchema?.fields ?? [])
        .slice()
        .sort((a, b) => (a.name || a.fieldId).localeCompare(b.name || b.fieldId))
        .map((tf) => ({
          kind: 'taxonomy' as const,
          id: tf.fieldId,
          label: tf.name || tf.fieldId,
        }))
    )
  );

  // Seed groupField exactly once when activeTemplate transitions from null
  // to non-null. This is the fix for Blockers 2 and 3 from 33-VERIFICATION.md.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return; // Only seed once per mount.
    if (!activeTemplate) return; // Template still loading — wait.
    if (groupField !== null) {
      // Already seeded synchronously at mount (rare hot path).
      seededRef.current = true;
      return;
    }
    const resolved = resolveGroupField(
      activeTemplate,
      availableGroupFields.filter((f) => f.kind === 'taxonomy')
    );
    if (resolved) {
      setGroupField(resolved);
      seededRef.current = true;
    }
    // resolveGroupField is defined at render time but its inputs are passed
    // explicitly, so the lint rule's conservative suggestion to include it
    // would just cause effect-churn. Keep deps to the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTemplate, availableGroupFields, groupField]);

  // Reconciliation: if the active template/project changes after initial
  // load and the current groupField is no longer in availableGroupFields,
  // reset to the first available option. MUST bail out while template is
  // loading — see Blocker 3 in 33-VERIFICATION.md.
  useEffect(() => {
    if (!activeTemplate) return; // Don't reconcile while loading.
    if (!groupField) return; // Not yet seeded.
    const first = availableGroupFields[0];
    if (!first) return;
    const stillValid = availableGroupFields.some(
      (f) => f.kind === groupField.kind && f.id === groupField.id
    );
    if (!stillValid) {
      setGroupField({ kind: first.kind, id: first.id });
    }
  }, [activeTemplate, availableGroupFields, groupField]);

  const [shareBasis, setShareBasis] = useState<ShareBasis>(
    searchParams.get('shareBasis') === 'spend' ? 'spend' : 'count'
  );

  const [hideNoData, setHideNoData] = useState(false);

  // Call useComposition UNCONDITIONALLY with a stable sentinel so hook ordering
  // is preserved across renders. The sentinel `{ kind: 'dimension', id: '' }`
  // is chosen because useDashboard.ts line 177 has `enabled: !!groupField.id`
  // which short-circuits the query when the id is empty — no wire request fires
  // while the template is still loading.
  const { data, isLoading, isError } = useComposition(
    filters,
    selectedKpiId,
    groupField ?? { kind: 'dimension', id: '' },
    shareBasis
  );

  // AFTER hooks, before render: early return while groupField is still null
  // (template hasn't arrived yet). This is safe — rules-of-hooks only forbid
  // skipping hooks, not skipping the render branch that follows. All hooks
  // above have already been called in a stable order.
  if (!groupField) {
    return <SkeletonCard lines={8} />;
  }

  const compositionData = data;

  if (isLoading) {
    return <SkeletonCard lines={8} />;
  }

  if (isError || !compositionData) {
    return (
      <EmptyState
        title="No portfolio segments yet"
        description="Analyze creatives with diagnosis dimensions to see portfolio composition."
      />
    );
  }

  const segments = compositionData.segments;
  // For creative kind, each segment has exactly 1 creative — skip the >=3 filter.
  const isCreativeKind = groupField.kind === 'creative';
  const filteredSegments = isCreativeKind
    ? segments
    : segments.filter((s) => s.creativeCount >= 3);
  const qualifyingSegments = hideNoData
    ? filteredSegments.filter((s) => s.kpiValue != null && s.kpiValue !== 0)
    : filteredSegments;
  // Variance check uses filteredSegments (structural), not qualifyingSegments
  // (which includes the display-only hideNoData filter).
  const isZeroVariance = isCreativeKind
    ? segments.length === 0
    : filteredSegments.length < 3;
  const hasSpendData = compositionData?.hasSpendData ?? false;

  // PCFG-06: precompute empty-state label + alternatives (used only when
  // isZeroVariance). Keep these outside the JSX for readability.
  const currentLabel =
    availableGroupFields.find(
      (f) => f.kind === groupField.kind && f.id === groupField.id
    )?.label ?? groupField.id;
  const qualifyingCount = qualifyingSegments.length;
  const alternatives = availableGroupFields.filter(
    (f) => !(f.kind === groupField.kind && f.id === groupField.id)
  );

  // Compute averages (safe when empty — unused unless chart branch renders)
  const avgX = qualifyingSegments.length > 0
    ? qualifyingSegments.reduce((sum, s) => sum + s.share, 0) / qualifyingSegments.length
    : 0;
  const avgY = qualifyingSegments.length > 0
    ? qualifyingSegments.reduce((sum, s) => sum + s.kpiValue, 0) / qualifyingSegments.length
    : 0;

  // Build chart points
  const chartData: DotPayload[] = qualifyingSegments.map((seg) => {
    const x = seg.share;
    const y = seg.kpiValue;
    const isUnderRepHighPerf = x < avgX && y > avgY;
    const isOverRepLowPerf = x > avgX && y < avgY;
    const color = isUnderRepHighPerf ? '#22C55E' : isOverRepLowPerf ? '#EF4444' : '#2563FF';
    return {
      ...seg,
      x,
      y,
      isSmall: seg.smallSample,
      color,
    };
  });

  // CSV export data
  const csvHeaders = ['Segment', 'Field', 'Share (%)', 'KPI Value', 'Creative Count', 'Small Sample'];
  const csvRows = qualifyingSegments.map((s) => [
    s.value,
    s.fieldName,
    +(s.share * 100).toFixed(1),
    s.kpiValue,
    s.creativeCount,
    s.smallSample ? 'Yes' : 'No',
  ]);

  return (
    <div className="rounded-[22px] border border-border-subtle bg-surface shadow-sm">
      {/* Header — UNCONDITIONAL, rendered for BOTH empty-state and chart branches.
          Phase 33 Plan 33-05 / Blocker 1: the dropdown used to be nested inside
          the chart branch and was unreachable when qualifyingSegments.length < 3,
          which is the majority path for the zero-variance dataset that defines
          this phase. Lifting the header above the body fixes PCFG-01. */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div>
          <div className="text-lg font-semibold text-primary">Portfolio Composition vs Performance</div>
          <div className="text-xs text-muted mt-0.5">
            Each dot = a creative segment. Quadrant shows production focus vs KPI performance.
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* GroupField dropdown (PCFG-01) — visually grouped as Dimensions / Taxonomy / Placement */}
          <select
            value={`${groupField.kind}::${groupField.id}`}
            onChange={(e) => {
              const [kind, id] = e.target.value.split('::') as [GroupField['kind'], string];
              setGroupField({ kind, id });
            }}
            className="text-xs border border-border-subtle rounded-[10px] px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand max-w-[220px]"
            aria-label="Group by field"
          >
            <optgroup label="Dimensions">
              {availableGroupFields.filter((f) => f.kind === 'dimension').length === 0 && (
                <option disabled value="__no_dimensions__">
                  No dimensions
                </option>
              )}
              {availableGroupFields
                .filter((f) => f.kind === 'dimension')
                .map((f) => (
                  <option key={`dimension::${f.id}`} value={`dimension::${f.id}`}>
                    {f.label}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Taxonomy">
              {availableGroupFields.filter((f) => f.kind === 'taxonomy').length === 0 && (
                <option disabled value="__no_taxonomy__">
                  No taxonomy fields
                </option>
              )}
              {availableGroupFields
                .filter((f) => f.kind === 'taxonomy')
                .map((f) => (
                  <option key={`taxonomy::${f.id}`} value={`taxonomy::${f.id}`}>
                    {f.label}
                  </option>
                ))}
            </optgroup>
            <optgroup label="Individual">
              <option value="creative::all">All Creatives</option>
            </optgroup>
            <optgroup label="Placement">
              <option value="placement::placement">Meta Placement</option>
            </optgroup>
          </select>

          {/* KPI dropdown (FR-42) */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted">Y-axis:</span>
            <select
              value={selectedKpiId}
              onChange={(e) => setSelectedKpiId(e.target.value)}
              className="text-xs border border-border-subtle rounded-[10px] px-2 py-1 bg-surface text-primary focus:outline-none focus:ring-1 focus:ring-brand max-w-[200px]"
            >
              {META_KPI_CATALOG.map((kpi) => (
                <option key={kpi.id} value={kpi.id}>
                  {kpi.label}{kpi.id === primaryKpiId ? ' * Primary' : ''}
                </option>
              ))}
              <option value="gatePassRate">Gate Pass Rate</option>
            </select>
          </div>

          {/* Share basis toggle (FR-41 / PCFG-03 — shareBasis now flows into useComposition) */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setShareBasis('count')}
              className={cn(
                'px-2.5 py-1 rounded-l-md text-xs font-medium border transition-colors',
                shareBasis === 'count'
                  ? 'bg-brand text-white border-brand'
                  : 'bg-surface text-secondary border-border-subtle hover:bg-surface-subtle'
              )}
            >
              By count
            </button>
            <button
              onClick={() => hasSpendData && setShareBasis('spend')}
              disabled={!hasSpendData}
              title={!hasSpendData ? 'Connect Meta to enable' : undefined}
              className={cn(
                'px-2.5 py-1 rounded-r-md text-xs font-medium border transition-colors',
                shareBasis === 'spend'
                  ? 'bg-brand text-white border-brand'
                  : 'bg-surface text-secondary border-border-subtle hover:bg-surface-subtle',
                !hasSpendData && 'opacity-40 cursor-not-allowed'
              )}
            >
              By spend
            </button>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={hideNoData}
              onChange={(e) => setHideNoData(e.target.checked)}
              className="rounded border-border-subtle text-brand focus:ring-brand h-3.5 w-3.5"
            />
            <span className="text-xs text-muted">Hide no-data</span>
          </label>
          <CsvExport headers={csvHeaders} rows={csvRows} filename="portfolio-composition" />
        </div>
      </div>

      {/* Body — branches between zero-variance empty state and populated chart */}
      {isZeroVariance ? (
        <div className="p-8">
          <div className="text-center">
            <PieChart size={24} className="mx-auto text-muted mb-2" />
            <div className="text-sm font-semibold text-secondary">
              Not enough variance in {currentLabel}
            </div>
            <div className="text-xs text-muted mt-1 max-w-md mx-auto">
              Composition needs at least 3 distinct values of <em>{currentLabel}</em>,
              each with 3+ creatives. Your current data has {qualifyingCount} qualifying value
              {qualifyingCount !== 1 ? 's' : ''}.
            </div>
          </div>
          {alternatives.length > 0 && (
            <div className="mt-4 border-t border-border-subtle pt-4">
              <div className="text-xs font-semibold text-secondary mb-2 text-center">
                Try a different grouping field:
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {alternatives.map((alt) => (
                  <button
                    key={`${alt.kind}::${alt.id}`}
                    onClick={() => setGroupField({ kind: alt.kind, id: alt.id })}
                    className="text-xs px-2 py-1 rounded-md border border-border-subtle bg-surface hover:bg-surface-subtle text-primary"
                  >
                    {alt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {alternatives.length === 0 && (
            <div className="mt-4 text-xs text-muted text-center">
              No grouping field has enough variance yet — add more creatives with different characteristics.
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-4">
          <div className="relative">
            {/* Quadrant labels (FR-43) */}
            <div className="absolute inset-0 pointer-events-none z-10" style={{ left: 50, right: 30, top: 10, bottom: 40 }}>
              <div className="absolute top-2 left-4 text-[10px] text-success font-medium opacity-60">
                Under-represented
              </div>
              <div className="absolute top-2 right-4 text-[10px] text-brand font-medium opacity-60 text-right">
                Well-focused
              </div>
              <div className="absolute bottom-12 left-4 text-[10px] text-muted font-medium opacity-60">
                Correctly minimal
              </div>
              <div className="absolute bottom-12 right-4 text-[10px] text-danger font-medium opacity-60 text-right">
                Over-represented
              </div>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <ScatterChart margin={{ top: 20, right: 40, bottom: 40, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7ECF6" />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Share (%)"
                  tick={{ fontSize: 10, fill: '#8B95A7' }}
                  tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  domain={[0, (dataMax: number) => dataMax * 1.15]}
                >
                  <Label
                    value={shareBasis === 'count' ? 'Share of Portfolio (%)' : 'Share of Spend (%)'}
                    offset={-10}
                    position="insideBottom"
                    style={{ fontSize: 11, fill: '#8B95A7' }}
                  />
                </XAxis>
                <YAxis
                  type="number"
                  dataKey="y"
                  domain={[0, (dataMax: number) => dataMax * 1.15]}
                  name={compositionData.kpiLabel}
                  tick={{ fontSize: 10, fill: '#8B95A7' }}
                  tickFormatter={(v: number) => fmtKpiValue(v, kpiUnit)}
                  width={60}
                >
                  <Label
                    value={compositionData.kpiLabel}
                    angle={-90}
                    position="insideLeft"
                    style={{ fontSize: 11, fill: '#8B95A7', textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const first = payload[0];
                    if (!first) return null;
                    const d = first.payload as DotPayload;
                    return (
                      <div className="rounded-[10px] bg-surface shadow-lg border border-border-subtle p-2 text-xs">
                        <div className="font-semibold text-primary">{d.value}</div>
                        <div className="text-muted">{fieldLabelMap[d.fieldName] ?? d.fieldName}</div>
                        <div className="mt-1">
                          <div>Share: {fmtPct(d.x * 100)}</div>
                          <div>Avg KPI: {fmtKpiValue(d.y, META_KPI_CATALOG.find(k => k.id === compositionData.kpiId)?.unit ?? '%')}</div>
                          <div>Creatives: {d.creativeCount}</div>
                          {d.isSmall && (
                            <div className="text-warning mt-0.5">Small sample (&lt;5)</div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                {/* Quadrant backgrounds */}
                <ReferenceArea x1={0} x2={avgX} y1={avgY} y2={9999} fill="rgba(35, 193, 107, 0.05)" />
                <ReferenceArea x1={avgX} x2={9999} y1={0} y2={avgY} fill="rgba(255, 77, 79, 0.05)" />
                {/* Reference lines */}
                <ReferenceLine x={avgX} stroke="#8B95A7" strokeDasharray="4 4" strokeWidth={1} />
                <ReferenceLine y={avgY} stroke="#8B95A7" strokeDasharray="4 4" strokeWidth={1} />
                <Scatter data={chartData} shape={<CustomDot />} />
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 justify-center mt-2 text-xs text-muted">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-success" />Under-represented opportunity
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-danger" />Over-represented & underperforming
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-brand" />Other
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#8B95A7] opacity-50" />Small sample (&lt;5)
            </span>
          </div>

          {/* Auto-insight sentence (restored from prototype) */}
          {(() => {
            const underRep = chartData.filter((d) => d.color === '#22C55E');
            const overRep = chartData.filter((d) => d.color === '#EF4444');
            if (underRep.length === 0 && overRep.length === 0) return null;
            return (
              <div className="mt-3 rounded-[10px] bg-surface-subtle px-3 py-2">
                <span className="text-xs text-secondary">
                  {underRep.length > 0 && underRep[0] && (
                    <><strong>{underRep[0].value}</strong> is under-represented but outperforming — consider producing more.{' '}</>
                  )}
                  {overRep.length > 0 && overRep[0] && (
                    <><strong>{overRep[0].value}</strong> has disproportionate share vs its performance.</>
                  )}
                </span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
